'use client';

import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { MotionSafeVideo } from '@/components/common/MotionSafeVideo';
import { PhaseCarouselCard } from '@/components/redesign/PhaseCarouselCard';
import { useCardCarousel } from '@/hooks/useCardCarousel';

type RedesignSlice = Extract<
  Content.ProcessOverviewThreeStepSlice,
  { variation: 'redesignCarousel' }
>;

export type ProcessOverviewThreeStepRedesignProps =
  SliceComponentProps<RedesignSlice>;

const ProcessOverviewThreeStepRedesign: FC<
  ProcessOverviewThreeStepRedesignProps
> = ({ slice }) => {
  const { title, description, background_image, steps } = slice.primary;
  const { carouselRef, scrollEdge, scrollByCard } = useCardCarousel();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-[#262626] py-16 md:py-20 lg:py-[70px]"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          sizes="100vw"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fallbackAlt=""
        />
      )}
      <div className="relative flex flex-col gap-9">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-5 md:px-[30px]">
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[28px] font-medium leading-[1.2] text-white md:text-[42px]">
                    {children}
                  </h2>
                ),
                strong: ({ children }) => (
                  <strong className="font-medium text-[#05f273]">
                    {children}
                  </strong>
                ),
              }}
            />
          )}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="w-full text-[16px] font-normal leading-[1.4] text-white/75">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        <div className="relative">
          <div
            ref={carouselRef}
            className="flex w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div
              aria-hidden
              className="w-5 shrink-0 md:w-[max(30px,calc((100%-1280px)/2+30px))]"
            />
            {(steps ?? []).map((step, index) => {
              const hasMedia =
                isFilled.linkToMedia(step.phase_video) ||
                isFilled.image(step.phase_image);
              return (
                <div
                  key={index}
                  data-phase-card
                  className={`flex w-[92%] shrink-0 flex-col overflow-hidden rounded-[20px] sm:w-[85%] md:items-stretch ${
                    hasMedia
                      ? 'lg:w-[min(1280px,calc(100vw-60px))] lg:flex-row xl:w-[1280px]'
                      : 'md:w-[496px]'
                  } ${index === 0 ? '' : 'ml-9'}`}
                >
                  <PhaseCarouselCard
                    // At lg (1024–1279) the card row is capped smaller than
                    // its designed 1280px, so the fixed 496px content column
                    // leaves the media column tight. Trim ~30% off the
                    // content column at lg to hand that width back to the
                    // video (flex-1). xl restores the designed 496px.
                    className="md:w-full lg:w-[340px] xl:w-[496px]"
                    icon={step.phase_icon}
                    title={step.phase_title}
                    description={step.phase_description}
                    cta={step.phase_cta}
                    atStart={scrollEdge.atStart}
                    atEnd={scrollEdge.atEnd}
                    onPrev={() => scrollByCard(-1)}
                    onNext={() => scrollByCard(1)}
                    mediaSlot={
                      isFilled.linkToMedia(step.phase_video) ? (
                        <div className="relative min-h-[280px] flex-1 lg:min-h-[427px]">
                          <MotionSafeVideo
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            poster={
                              isFilled.image(step.phase_image)
                                ? (step.phase_image.url ?? undefined)
                                : undefined
                            }
                            className="absolute inset-0 h-full w-full object-cover"
                          >
                            <source src={step.phase_video.url} />
                          </MotionSafeVideo>
                        </div>
                      ) : (
                        isFilled.image(step.phase_image) && (
                          <div className="relative min-h-[280px] flex-1 lg:min-h-[427px]">
                            <PrismicNextImage
                              field={step.phase_image}
                              fill
                              sizes="(min-width: 768px) 784px, 100vw"
                              className="object-cover"
                              fallbackAlt=""
                            />
                          </div>
                        )
                      )
                    }
                  />
                </div>
              );
            })}
            <div
              aria-hidden
              className="w-5 shrink-0 md:w-[max(30px,calc((100%-1280px)/2+30px))]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessOverviewThreeStepRedesign;
